/**
 * Die eine Fortschrittsleiter der App.
 *
 * Der naechste Schritt wird aus gemessenen Zahlen ABGELEITET, nicht
 * verdrahtet. Nur so bleibt die Leiste in Sonderfaellen ehrlich.
 *
 * Reihenfolge der Regeln ist Absicht: unfreigegebene Arbeit steht vor neuer
 * Arbeit, weil unfreigegebene und nicht hochgeladene Optimierungen null Wert
 * haben (Strategie-Bericht 8.1b: der Freigabe-Schritt ist der Flaschenhals
 * des gesamten Beweisapparats).
 *
 * Seit dem 09.09.2026 hat diese Vorfahrt eine BENANNTE AUSNAHME, und sie hebt
 * die Begruendung darueber nicht auf, sondern liest sie genauer: der Satz aus
 * 8.1b handelt von einem Stapel, der den Beweisapparat verstopft. Ein
 * Restposten von 19 Produkten neben 15.269 optimierbaren ist kein solcher
 * Stapel; er kann gar nicht wachsen, weil die Menge, aus der er entstehen
 * koennte, um drei Groessenordnungen groesser ist. Die Ausnahme kippt deshalb
 * ausschliesslich die PRIMAERAKTION der Handlungskarte (`action`), waehrend die
 * Leiter (`ladder`) unveraendert die Freigabe vorn behaelt und die Freigabe als
 * Zweitaktion sichtbar bleibt. Siehe `APPROVE_FIRST_MAX_RATIO`.
 */
export type JourneyStepId =
  | 'connect'
  | 'optimize'
  | 'approve'
  | 'upload'
  | 'ads_connect'
  | 'ads_campaigns';
/**
 * `'upcoming'` ist neu (Spec 3.1): der Zustand von Schritt 6 (ads_campaigns),
 * der niemals `current` und niemals `done` wird -- Kampagnen aus Segmenten
 * sind heute Teaser, kein erreichbarer Schritt.
 */
export type JourneyStepState =
  | 'done'
  | 'current'
  | 'pending'
  | 'skipped'
  | 'error'
  | 'blocked'
  | 'upcoming';

/**
 * Die Grenze, unterhalb der die Freigabe ihre Vorfahrt in der HANDLUNGSKARTE
 * verliert: offene Freigaben geteilt durch optimierbare Produkte.
 *
 * Ein Prozent, und das ist kein Gefuehlswert, sondern die Grenze zwischen zwei
 * verschiedenen Dingen. Anlassfall ist golfimport, gemessen am 07.09.2026: 19
 * offene Freigaben zu 15.269 optimierbaren Produkten, Feed-Note F. Die Leiter
 * schickte den Nutzer dort auf die Freigabe von 19 Produkten, waehrend 15.269
 * Produkte unbearbeitet lagen und die Note deshalb F war. Das Verhaeltnis
 * betraegt 0,124 Prozent.
 *
 * Warum genau hier die Grenze liegt: ein Stapel offener Freigaben ist ein
 * Flaschenhals, solange er WACHSEN kann, also solange die Optimierungen, die
 * ihn speisen, in derselben Groessenordnung liegen wie er selbst. Unterhalb
 * eines Prozents ist das strukturell ausgeschlossen: der Zufluss ist um mehr
 * als das Hundertfache groesser als der Bestand, der Bestand ist also ein
 * RESTPOSTEN aus einem abgeschlossenen Lauf und keine Warteschlange. Ein
 * Restposten kostet einen Klick, ein Flaschenhals kostet den Beweisapparat.
 *
 * Gegenprobe an einem Konto, das die Grenze NICHT unterschreitet: Spielgigant
 * mit 20 offenen Freigaben zu 1.959 optimierbaren Produkten sind 1,02 Prozent.
 * Dort bleibt die Freigabe die Primaeraktion, obwohl die absolute Zahl fast
 * dieselbe ist. Genau das ist der Punkt: die Entscheidung haengt am
 * VERHAELTNIS, nicht an der absoluten Zahl. Eine absolute Schwelle ("unter 25
 * offene Freigaben") haette beide Konten gleich behandelt und damit den
 * Unterschied verwischt, um den es geht.
 */
export const APPROVE_FIRST_MAX_RATIO = 0.01;

export interface JourneyInput {
  hasMerchantCenter: boolean;
  syncFailed: boolean;
  lastSyncedAt: string | null;
  total: number;
  optimized: number;
  /**
   * Die OPTIMIERBARE Menge, nicht die rohe Zahl der Produkte mit
   * `optimization_status='not_optimized'`.
   *
   * Speisung ist `useProductStats.optimizable` (siehe die Zuweisung in
   * `useJourney.ts`): `not_optimized` PLUS `failed`, und beide durch das
   * Serving-Gate gefiltert, also genau die Menge, die
   * `enqueue-optimization-batches` serverseitig auch auswaehlen wuerde. Der
   * Name ist historisch; er beschreibt seit dem Serving-Gate (26.08.2026) nicht
   * mehr die Spalte, sondern das Angebot.
   *
   * Das ist zugleich der NENNER der Kipp-Regel in `deriveNextStep`
   * (`APPROVE_FIRST_MAX_RATIO`), und zwar der richtige: die Regel fragt "wie
   * viel Arbeit liegt neben dem Freigabe-Restposten", und Arbeit ist nur, was
   * der Server auch anfassen wuerde. Ein abgelehntes Produkt ohne heilbares
   * Issue zaehlt dort nicht mit, weil kein Optimieren-Angebot es umfasst.
   *
   * Ein ZWEITES Feld `optimizable` wird bewusst NICHT eingefuehrt, obwohl der
   * Name treffender waere: zwei Namen fuer denselben Wert sind eine
   * Drift-Quelle. Es gaebe dann zwei Eingaenge, die dasselbe bedeuten sollen,
   * und der naechste Umbau muesste jedes Mal entscheiden, welchen er speist.
   */
  notOptimized: number;
  /**
   * Der ROHE `approval_status='pending_review'`-Vorrat — der Arbeitsvorrat der
   * Werkbank, NICHT die Menge, die durch die Freigabe hindurch muss.
   *
   * ABSICHTLICH UNBENUTZT in dieser Ableitung. Kein Gate, keine Stufenzahl und
   * keine Unterzeile liest dieses Feld; gerechnet wird ausschliesslich mit
   * `pendingReviewAwaitingUpload`. Es bleibt trotzdem Teil der Eingabe, weil
   * die Testfixtures die gemessene Live-Form nur mit BEIDEN Zahlen beschreiben
   * koennen (live z.B. egli-plastic.ch CH: 1.055 roh, 0 in der Schnittmenge) —
   * und weil ein Pin genau darauf, dass dieses Feld die Ableitung nicht
   * bewegt, mehr wert ist als ein entfernter Eingang. Siehe den Test
   * "der rohe Vorrat allein bewegt die Ableitung nicht".
   *
   * Wer daraus wieder ein Gate macht, installiert die Doppelzaehlung von
   * Welle 8 zurueck: 10.409 Zeilen tragen live gleichzeitig `pending_review`
   * UND `upload_status='uploaded'` (5 Feeds, gemessen 2026-07-31), und die
   * Upload-Stufe zaehlt genau die eine Spalte weiter als live.
   */
  pendingReview: number;
  /**
   * Freigegeben UND noch nicht hochgeladen — NICHT die rohe Zahl der Produkte
   * mit `approval_status='approved'`.
   *
   * Der Unterschied ist der zwischen einer Leiter, die ankommt, und einer, die
   * es nie tut: `approval_status` wird nach einem erfolgreichen Upload nirgends
   * zurueckgesetzt (`process-upload-queue` fasst die Spalte nicht an, der
   * einzige Ruecksetzer ist die Re-Optimierung, und die schreibt
   * `pending_review`). Mit der rohen Zahl waere `approved > 0` ab dem ersten
   * Upload dauerhaft wahr; Regel 3 steht vor der Optimieren-Regel, also zeigte
   * der Zeiger fuer immer auf Hochladen und der Zustand "alles erledigt" waere
   * unerreichbar. Speisung: `useProductStats.approvedAwaitingUpload`.
   */
  approved: number;
  uploaded: number;
  needsReupload: number;
  /**
   * N7: `approval_status='rejected'`. Speisung: `useProductStats.rejected`.
   *
   * Seit Spec 3.1 (16.09.2026) an KEINEM Chip mehr sichtbar: "Abgelehnte haben
   * einen Reiter, aber keinen Chip." Bleibt trotzdem Teil der Eingabe, aus
   * demselben Grund wie `pendingReview` zwei Felder weiter unten -- ein
   * Pin-Test darauf, dass dieses Feld die Ableitung nicht bewegt, ist mehr
   * wert als ein entfernter Eingang, und die Werkbank braucht die Zahl fuer
   * ihren eigenen Reiter unabhaengig von der Leiter.
   */
  rejected: number;
  /**
   * Wartet auf Freigabe UND ist noch nicht bei Google. Speisung:
   * `useProductStats.pendingReviewAwaitingUpload`.
   *
   * Die EINE Zahl der Freigabe-Stufe: Gate, Unterzeile, Verlustzeile und
   * Handlungskarte lesen alle diese. `pendingReview` kann das nicht leisten,
   * weil `approval_status` nach einem Upload nirgends zurueckgesetzt wird —
   * ein einziger Upload aus der Werkbank genuegt, und der rohe Vorrat bleibt
   * danach dauerhaft > 0, obwohl die Produkte live sind.
   */
  pendingReviewAwaitingUpload: number;
  /**
   * Der Haken `auto_upload_new_enabled` / `auto_upload_drift_enabled` steht.
   *
   * ACHTUNG, das ist eine ABSICHT, kein Zustand: serverseitig gilt der
   * Auto-Upload ausschliesslich fuer AUTOMATIK-Laeufe
   * (`job-lifecycle.ts`: `triggered_by?.startsWith('auto-')`, und dort noch
   * einmal die zum Ausloeser passende Checkbox). Ein MANUELLER Lauf desselben
   * Nutzers laedt nie automatisch hoch. Dieses Flag darf deshalb nie allein
   * ueber den Freigabe-Schritt entscheiden — siehe `approvalSkipped`.
   */
  autoUpload: boolean;
  /**
   * N4: aus `check_can_optimize` v4 — `can_optimize` heisst dort seit dem
   * Bestandsmass "es ist Kapazitaet fuer NEUE Produkte frei", nicht mehr "das
   * Lifetime-Kontingent ist noch nicht aufgebraucht". Ein Konto, das seinen
   * betreuten Bestand ausschoepft, kann seine Produkte weiterhin erneut
   * optimieren; nur neue kommen nicht mehr dazu. Ohne diese beiden Felder zeigt
   * der Zeiger in die Blockade.
   */
  canOptimize: boolean;
  quotaPhase: 'GREEN' | 'YELLOW' | 'RED';
  /**
   * Schritt 5, der EINZIGE Zeiger unter den vier neuen Feldern (Spec 3.1,
   * Entscheidung E2 des Plans): ein Google-Ads-Konto ist mit dem Merchant
   * Center der Auswahl verknuepft. Wahr, wenn `deriveAdsChapterState` NICHT
   * `not_connected` und NICHT `unlinked` liefert -- also auch schon bei
   * "Zahlen folgen" (`no_numbers`). Die Wahrheit liegt in `isAdsLinkedState`
   * (src/lib/adsFeedSummary.ts); auch `owner_managed` gilt nicht als
   * verknuepft. Nur DIESES Feld bewegt die Ableitung; die
   * drei folgenden sind reines Anzeigematerial und tun es nicht.
   */
  adsLinked: boolean;
  /**
   * Reines Anzeigefeld fuer den Chip-Text von Schritt 5: unterscheidet
   * "verbunden" von "Zahlen folgen" (Spec 3, Zeile ads_connect). Bewegt die
   * Ableitung NICHT -- ein verknuepftes Konto ohne Zahlen ist bereits ein
   * fertiger Schritt 5 mit Wartezustand, kein offener; die Zahlen entscheiden
   * nie ueber den Zeiger, nur die Verknuepfung.
   */
  adsNumbersReady: boolean;
  /** Reines Anzeigefeld fuer den Tooltip von Schritt 5 (maskiert dargestellt). */
  adsCustomerId: string | null;
  /** Reines Anzeigefeld fuer den Tooltip von Schritt 4 (Live). */
  lastUploadAt: string | null;
  /**
   * WP5 K22: am Google-Konto haengt mindestens ein Ads-Konto, aber keines
   * zeigt auf dieses Merchant Center (Kapitelzustand `unlinked`). Reines
   * Anzeigefeld fuer das Chip-Wort von Schritt 5, bewegt die Ableitung nicht.
   * Optional, damit bestehende Fixtures gueltig bleiben; fehlt es, gilt false.
   */
  adsAccountConnected?: boolean;
  /**
   * A2-NEU-1: Der Betrachter ist Mitglied (nicht Eigentuemer) der MC, die
   * Ads-Verknuepfung liegt beim Eigentuemer. Bewegt die Ableitung: Schritt 5
   * ist dann weder offen noch erledigt (`skipped`) und nie der Zeiger.
   * `adsLinked` schlaegt das Feld. Optional, fehlt es, gilt false.
   */
  adsOwnerManaged?: boolean;
}

/**
 * Die zweite (und bei 'approve' frueher dritte) Zahl je Schritt, seit dem
 * Umbau auf sechs Schritte als diskriminierte Union statt zweier optionaler
 * Zahlenfelder (Entscheidung E1 des Plans, Spec Abschnitt 3/4).
 *
 * Die alten Felder `secondary`/`rejected` an `JourneyStep` reichten fuer die
 * neuen Zustandswoerter nicht mehr aus: Schritt 1 (Feed) braucht das
 * Sync-Alter, Schritt 4 (Live) die veraltete Zahl UND den letzten
 * Upload-Zeitpunkt, Schritt 5 (Anzeigen) die Kontonummer und den Stand der
 * Zahlen. Statt eine dritte, vierte, fuenfte optionale Zahl anzuhaengen,
 * bekommt jeder Schritt sein eigenes, typisiertes `detail` mit genau den
 * Feldern, die sein Tooltip braucht (Abschnitt 4: "die Zustandschips zeigen
 * ein Wort, ihre Zahlen stehen im Tooltip").
 *
 * Die frueheren `secondary`/`rejected`-Zahlen der Freigabe-Stufe sind darin
 * NICHT eins-zu-eins wiederzufinden: die Wartenden (`secondary`) sind seit
 * Spec 3.1 die Hauptzahl von Schritt 2 (`count`/`detail.toReview` bei
 * 'optimize'), und die Abgelehnten (`rejected`) haben einen Reiter in der
 * Werkbank, aber keinen Chip mehr -- sie stehen an keinem Schritt.
 */
export type JourneyStepDetail =
  | { kind: 'connect'; total: number; lastSyncedAt: string | null; hasMerchantCenter?: boolean }
  | { kind: 'optimize'; optimized: number; toReview: number }
  | { kind: 'approve'; ready: number; toApprove?: number }
  | { kind: 'upload'; live: number; stale: number; lastUploadAt: string | null }
  | { kind: 'ads_connect'; linked: boolean; numbersReady: boolean; customerId: string | null; accountConnected?: boolean; ownerManaged?: boolean }
  | { kind: 'ads_campaigns' };

export interface JourneyStep {
  id: JourneyStepId;
  state: JourneyStepState;
  /** Gemessene Zahl fuer diesen Schritt. Vorgabe 3.2: nie ein blinder Balken. */
  count: number;
  /** Die Tooltip-Daten dieses Schritts, siehe `JourneyStepDetail`. */
  detail: JourneyStepDetail;
}

const ORDER: JourneyStepId[] = [
  'connect',
  'optimize',
  'approve',
  'upload',
  'ads_connect',
  'ads_campaigns',
];

/**
 * Die Menge, die durch die Freigabe HINDURCH muss: wartet auf Freigabe UND ist
 * noch nicht bei Google.
 *
 * Bedingungslos gemessen, nie am Auto-Upload-Haken abgelesen. Der frueher hier
 * stehende Ternaer las ausserhalb der Automatik den rohen `pendingReview`-
 * Vorrat, unter der Annahme, beide Zahlen seien dort dasselbe. Diese Annahme
 * ist live widerlegt: 10.409 Zeilen tragen gleichzeitig `pending_review` UND
 * `upload_status='uploaded'`, verteilt auf 5 Feeds — und alle 5 gehoeren
 * Nutzern mit AUSGESCHALTETEM Haken (0 von 19 Profilen hat einen der beiden
 * Haken an; gemessen 2026-07-31). Es braucht keine Automatik dafuer, nur einen
 * einzigen Upload aus der Werkbank: `approval_status` wird danach nirgends
 * zurueckgesetzt. Auf egli-plastic.ch CH stehen so 1.055 Zeilen roh gegen 0 in
 * der Schnittmenge.
 *
 * Mit dem Vorrat behauptete die Leiter "1.055 warten auf Freigabe", waehrend
 * die Upload-Stufe eine Spalte weiter dieselben Produkte als live zaehlte, und
 * die Verlustzeile sagte fuer live liegende Produkte "wirken bei Google
 * nicht". Diese eine Zahl lesen jetzt `deriveNextStep`, die Verlustzeile des
 * Feed-Check-Kopfs und die Handlungskarte des Dashboards. Seit Spec 3 (Umbau
 * auf sechs Schritte, 16.09.2026) ist sie ausserdem die CHIP-ZAHL von Schritt
 * 2 (Optimieren, "N zu pruefen") — sie stand vorher als Unterzeile an der
 * Freigabe-Stufe, siehe die Ersetzung von `secondary`/`rejected` durch
 * `detail` unten.
 *
 * Der Haken bleibt genau dort stehen, wo die Frage nicht "wie viele" ist,
 * sondern "WER hat uebersprungen" — siehe `approvalSkipped`.
 */
function awaitingApproval(i: JourneyInput): number {
  return i.pendingReviewAwaitingUpload;
}

/**
 * Die Freigabe ist NUR dann wirklich uebersprungen, wenn gemessen nichts
 * mehr durch sie hindurch muss.
 *
 * Am Haken `autoUpload` allein aufgehaengt war das eine Falschaussage, sobald
 * der Nutzer bei gesetztem Haken MANUELL optimierte: der Auto-Upload greift
 * serverseitig nur fuer Automatik-Laeufe, die manuell erzeugten Produkte
 * bleiben `pending_review` und brauchen die Werkbank. Die Leiter wies die
 * Freigabe trotzdem als "uebersprungen / 0" aus, unterdrueckte die Wartezahl
 * UND den Freigeben-Knopf, waehrend die Verlustzeile eine Zeile darunter
 * dieselben Produkte als "wirken bei Google nicht" nannte. Der einzige
 * Primaer-CTA startete dann einen WEITEREN Lauf — Lifetime-Kontingent ohne
 * Reset-Pfad, waehrend die bezahlte Arbeit unsichtbar liegen blieb.
 *
 * `approved === 0` gehoert dazu, weil 'skipped' die Hauptzahl der Stufe in der
 * JourneyBar durch das Wort "uebersprungen" ERSETZT: nach der Freigabe stand
 * dort sonst wieder nichts, waehrend Handlungskarte und Werkbank-Knopf im
 * selben Moment "N freigegebene Produkte zu Google uebertragen" nannten.
 *
 * Bewusst in Kauf genommen: waehrend ein Automatik-Upload noch in der Queue
 * liegt, ist die Schnittmenge > 0 und der Zeiger steht auf der Freigabe. Die
 * Aussage ist in diesem Fenster wahr (die Produkte sind nicht bei Google), und
 * ein Klick dorthin richtet keinen Schaden an. Nicht "kurz": `useProductStats`
 * pollt nur waehrend eines laufenden Optimierungsjobs (`refetchInterval` sonst
 * false), der Zustand haelt also bis zum naechsten Remount oder Routenwechsel.
 * Bei den naechtlichen Automatik-Laeufen sieht das niemand; wer waehrend eines
 * manuell ausgeloesten Auto-Laufs auf der Seite bleibt, sieht die Stufe stehen.
 * Ein Polling-Fenster ueber das Job-Ende hinaus waere der saubere Zug und liegt
 * im Backlog.
 *
 * Das `i.autoUpload`-Gate bleibt HIER und ist aus `awaitingApproval`
 * verschwunden — kein Widerspruch, sondern zwei verschiedene Fragen. Dort geht
 * es um "wie viele muessen durch die Freigabe", und die Antwort ist immer eine
 * Messung. Hier geht es um "WER hat sie uebersprungen", und das ist eine Frage
 * nach der Absicht: ohne gesetzten Haken hat niemand etwas uebersprungen, ein
 * leerer Vorrat heisst dann "erledigt" (siehe `stateFor`), nicht
 * "uebersprungen".
 */
function approvalSkipped(i: JourneyInput): boolean {
  return i.autoUpload && awaitingApproval(i) === 0 && i.approved === 0;
}

/**
 * Zwei verschiedene Aussagen aus derselben Messung.
 *
 * `ladder` ist die Fortschrittsleiter: welcher der sechs Schritte ist der
 * naechste. Sie speist die `JourneyChips` im Rahmen und ueber `stateFor` deren
 * `current`-Zustand, und sie kippt nie (Entscheidung D1).
 *
 * `action` ist die PRIMAERAKTION der Handlungskarte des Feed-Checks. Sie ist in
 * der Regel derselbe Schritt und weicht nur im Restposten-Fall ab (siehe
 * `APPROVE_FIRST_MAX_RATIO`). `actionSecondary` traegt dann den Schritt, der
 * seine Vorfahrt abgegeben hat, damit er als Zweitaktion sichtbar bleibt statt
 * zu verschwinden; `null` heisst "es gibt keine Zweitaktion".
 */
interface JourneyDirection {
  ladder: JourneyStepId | null;
  action: JourneyStepId | null;
  actionSecondary: JourneyStepId | null;
}

function deriveLadderStep(i: JourneyInput): JourneyStepId | null {
  if (!i.hasMerchantCenter || i.syncFailed) return 'connect';
  // Vorgabe 3.2/3.3: ein frisch verbundener, nie gesynchter Feed hat keinen
  // Sync-Zeitpunkt UND null Produkte. Ohne diese Regel faellt notOptimized
  // ebenfalls auf 0 und die Leiter zeigt "alles erledigt" am allerersten Tag,
  // bevor ueberhaupt ein Sync gelaufen ist.
  // Gegenprobe bewusst mit total === 0 verknuepft: fehlt lastSyncedAt bei
  // vorhandenen Produkten (Alt-Daten ohne rueckwirkend befuellten Timestamp),
  // ist der Feed nachweislich schon gesynct und gilt NICHT als unsynct.
  if (i.lastSyncedAt === null && i.total === 0) return 'connect';
  // Gemessen, nicht am Haken abgelesen: `awaitingApproval` faellt nur dann auf
  // 0, wenn die wartenden Produkte nachweislich schon bei Google sind — egal
  // ob eine Automatik oder ein Upload aus der Werkbank sie dorthin gebracht
  // hat.
  if (awaitingApproval(i) > 0) return 'approve';
  // NUR `approved` (= freigegeben UND noch nicht hochgeladen) macht den Upload
  // zum naechsten Schritt. `needsReupload > 0` allein reicht bewusst NICHT:
  // ein re-optimiertes Produkt steht auf pending_review (Regel oben greift),
  // und ein danach ABGELEHNTES behaelt upload_status='needs_reupload' bei
  // approval='rejected' — es gibt dann nichts Freigegebenes zu uebertragen.
  // Mit der alten Regel bot die Handlungskarte "0 freigegebene Produkte zu
  // Google uebertragen" an und schickte in eine leere Werkbank ohne
  // Upload-Knopf. Approved-gewordene needs_reupload-Produkte zaehlen in
  // `approved` mit (approvedAwaitingUpload = approved UND nicht 'uploaded').
  if (i.approved > 0) return 'upload';
  if (i.notOptimized > 0) return 'optimize';
  // Spec 3.1: mit den ersten vier Schritten fertig endet die Leiter seit dem
  // Umbau auf sechs Schritte nicht mehr hier. Ohne verknuepftes Google-Ads-
  // Konto ist Schritt 5 (Anschluss) der naechste; erst danach -- und nur dann
  // -- gibt es tatsaechlich keinen naechsten Schritt mehr. Schritt 6
  // (Kampagnen) hat keinen erreichbaren Zeiger, siehe `stateFor`.
  if (!i.adsLinked && !i.adsOwnerManaged) return 'ads_connect';
  return null;
}

function deriveNextStep(i: JourneyInput): JourneyDirection {
  const ladder = deriveLadderStep(i);

  // Die Restposten-Ausnahme (Entscheidung Dennis 07.09.2026). Alle vier
  // Bedingungen muessen gelten, und keine davon ist entbehrlich:
  //
  //   1. `ladder === 'approve'` -- nur die Vorfahrt der Freigabe wird
  //      aufgeweicht. Die Regeln davor (kein Merchant Center, nie gesynct)
  //      beschreiben einen Feed, der noch gar nichts hat, und die Upload-Regel
  //      danach beschreibt bezahlte Arbeit, die einen Klick vor dem Wirken
  //      steht. Beide kippen nie.
  //   2. `pendingReviewAwaitingUpload > 0` -- die Zahl, um deren Vorfahrt es
  //      geht. Bei 0 stuende `ladder` ohnehin nicht auf 'approve'; die
  //      Bedingung macht das ausdruecklich, statt es aus Regel 1 zu folgern.
  //   3. `notOptimized > 0` -- und das ist AUSDRUECKLICH NICHT nur ein Schutz
  //      gegen die Division durch Null. Ohne optimierbare Produkte gibt es kein
  //      Optimieren-Angebot, auf das gekippt werden koennte: die Karte bekaeme
  //      als Primaeraktion einen Schritt, der 0 Produkte betrifft, und der
  //      Nutzer stuende vor einem Knopf ohne Menge. Der Nulldivisions-Schutz
  //      fiele hier nebenbei mit an, waere aber die schwaechere Begruendung.
  //   4. das Verhaeltnis liegt unter `APPROVE_FIRST_MAX_RATIO`.
  //   5. `canOptimize` -- es gibt Kapazitaet fuer neue Produkte. Ohne sie ist
  //      das Optimieren-Angebot LEER, obwohl `notOptimized` gross ist: die
  //      angebotene Menge ist `min(remainingCapacity, optimizable)`, und bei
  //      erschoepftem Bestandsmass ist der erste Faktor 0. Die Karte fiel dann
  //      in ihren Done-Zustand ("Nichts wartet auf dich"), waehrend die
  //      Freigabe-Stufe eine Zeile darueber 19 offene Freigaben zeigte -- der
  //      Zustand, gegen den die ganze Regel gebaut ist, nur umgekehrt.
  //      `quotaBlocked` konnte das nicht abfangen: es haengt begruendet an
  //      `nextStep` (der Leiter, D1), und die steht im Kipp-Fall auf 'approve'.
  //      Die Bedingung gehoert deshalb hierher, wo die Regel entscheidet, und
  //      nicht in einen zweiten Riegel an der Karte.
  const isApprovalRemnant =
    ladder === 'approve' &&
    i.pendingReviewAwaitingUpload > 0 &&
    i.notOptimized > 0 &&
    i.canOptimize &&
    i.pendingReviewAwaitingUpload / i.notOptimized < APPROVE_FIRST_MAX_RATIO;

  if (isApprovalRemnant) {
    return { ladder, action: 'optimize', actionSecondary: 'approve' };
  }

  return { ladder, action: ladder, actionSecondary: null };
}

function countFor(id: JourneyStepId, i: JourneyInput): number {
  switch (id) {
    case 'connect': return i.total;
    // Seit Spec 3 (Umbau auf sechs Schritte, 16.09.2026) die WARTENDE Menge
    // ("N zu pruefen"), nicht mehr der optimierte Bestand: die Chip-Zahl von
    // Schritt 2 ist jetzt `pendingReviewAwaitingUpload`, dieselbe Zahl, die
    // vorher als `secondary` an der Freigabe-Stufe stand. Der optimierte
    // Bestand bleibt gemessen, steht aber nur noch im `detail` dieses Schritts
    // (`optimized`) und nicht mehr am Chip.
    case 'optimize': return i.pendingReviewAwaitingUpload;
    // F3: der BESTAND freigegebener (und noch nicht hochgeladener) Produkte —
    // dieselbe Groesse, die der Werkbank-Kopf als "N freigegebene Produkte zu
    // Google uebertragen" nennt. Die Wartenden stehen seit dem Umbau NICHT
    // mehr hier, sondern am Chip von Schritt 2 (siehe oben).
    // Kein Klemmen auf 0 im Automatik-Modus mehr: `approved` IST die gemessene
    // Freigabe-Menge und damit von selbst 0, wenn sie leer ist. Die alte
    // Klemme konnte nur eines: eine echte Zahl verstecken — genau die, die
    // Handlungskarte und Werkbank-Knopf im selben Moment nennen.
    case 'approve': return i.approved;
    // needs_reupload zaehlt mit: das Produkt IST bei Google live. Der Status
    // bedeutet laut Schema "war hochgeladen, wurde seitdem erneut optimiert" —
    // die alte Fassung liegt weiter im Merchant Center und wirkt dort. Nur
    // `uploaded` zu zaehlen haette den letzten Schritt genau in dem Moment
    // untertrieben, in dem der Nutzer am fleissigsten war, und die Leiste
    // dementiert damit die Zahl, die sie eine Zeile weiter oben unter
    // "Freigegeben" selbst erzeugt hat.
    case 'upload': return i.uploaded + i.needsReupload;
    // Schritt 5 und 6 sind Zustandschips (Spec 4): ihr Chip-Text ist ein Wort,
    // keine Zahl. Die tatsaechlichen Zahlen (Kontonummer, Zahlenstand) stehen
    // in `detail`.
    case 'ads_connect': return 0;
    case 'ads_campaigns': return 0;
  }
}

function stateFor(id: JourneyStepId, i: JourneyInput, next: JourneyStepId | null): JourneyStepState {
  // Schritt 6 (Spec 3.1): eigene Marke, nie 'current', nie 'done'. Kampagnen
  // aus Segmenten sind heute ein Teaser -- `deriveLadderStep` gibt diesen
  // Wert nie als naechsten Schritt zurueck, `next` kann ihn also ohnehin nie
  // treffen, aber die Regel steht hier explizit statt implizit ueber den
  // generischen `id === next`-Fall.
  if (id === 'ads_campaigns') return 'upcoming';

  if (id === 'connect') {
    if (i.syncFailed) return 'error';
    // Vorgabe 3.3: nie bei 0 von N starten. Ein gesyncter Feed hat Schritt 1
    // erledigt, und zwar bevor der Nutzer irgendetwas getan hat.
    if (!i.hasMerchantCenter) return 'current';
    // Deckt sich mit der Regel in deriveNextStep: ein nie gesynchter Feed
    // (kein Zeitpunkt, null Produkte) ist Schritt 1 noch nicht "done".
    if (i.lastSyncedAt === null && i.total === 0) return 'current';
    return 'done';
  }

  // Der Automatik-Modus ueberspringt die Freigabe — aber nur, wenn er es
  // nachweislich getan hat. Ein Haken an einer nie erledigten Pruefung waere
  // eine Falschaussage, "uebersprungen" an einer offenen genauso.
  if (id === 'approve' && approvalSkipped(i)) return 'skipped';

  // Schritt 5 (Spec 3.1): `done`, sobald ein Ads-Konto verknuepft ist -- das
  // gilt unabhaengig davon, ob der Zeiger gerade hier steht, denn ein
  // verknuepftes Konto kann `next` gar nicht mehr sein (`deriveLadderStep`
  // gibt 'ads_connect' nur bei `!adsLinked && !adsOwnerManaged` zurueck).
  // Bei einem Mitglied liegt die Verknuepfung beim Eigentuemer: Schritt 5 ist
  // dort weder offen noch erledigt, also `skipped`. Sonst entscheidet wie
  // ueberall der Zeiger.
  if (id === 'ads_connect') {
    return i.adsLinked ? 'done' : i.adsOwnerManaged ? 'skipped' : (id === next ? 'current' : 'pending');
  }

  if (id === next) return 'current';

  switch (id) {
    case 'optimize': return i.optimized > 0 ? 'done' : 'pending';
    case 'approve': return i.approved > 0 || i.uploaded > 0 ? 'done' : 'pending';
    case 'upload': return i.uploaded > 0 ? 'done' : 'pending';
    default: return 'pending';
  }
}

/**
 * Die Tooltip-Daten je Schritt (Spec 4: "die Zahl steht im Tooltip, wo der
 * Chip nur ein Wort zeigt"). Reine Ableitung wie `countFor`/`stateFor`, keine
 * zweite Wahrheit -- `optimize.toReview` ist dieselbe Zahl wie
 * `countFor('optimize', i)`, `upload.live` dieselbe wie `countFor('upload', i)`.
 */
function detailFor(id: JourneyStepId, i: JourneyInput): JourneyStepDetail {
  switch (id) {
    case 'connect':
      return { kind: 'connect', total: i.total, lastSyncedAt: i.lastSyncedAt, hasMerchantCenter: i.hasMerchantCenter };
    case 'optimize':
      return { kind: 'optimize', optimized: i.optimized, toReview: awaitingApproval(i) };
    case 'approve':
      // WP5 K23: die wartende Menge steht mit im Detail, damit Chip und
      // Tooltip sie nennen koennen, wenn der Zeiger hier steht und noch
      // nichts freigegeben ist.
      return { kind: 'approve', ready: i.approved, toApprove: awaitingApproval(i) };
    case 'upload':
      return {
        kind: 'upload',
        live: i.uploaded + i.needsReupload,
        stale: i.needsReupload,
        lastUploadAt: i.lastUploadAt,
      };
    case 'ads_connect':
      return {
        kind: 'ads_connect',
        linked: i.adsLinked,
        numbersReady: i.adsNumbersReady,
        customerId: i.adsCustomerId,
        accountConnected: i.adsAccountConnected === true,
        ownerManaged: i.adsOwnerManaged === true,
      };
    case 'ads_campaigns':
      return { kind: 'ads_campaigns' };
  }
}

export function deriveJourney(input: JourneyInput): {
  steps: JourneyStep[];
  nextStep: JourneyStepId | null;
  /**
   * Die Primaeraktion der Handlungskarte des Feed-Checks. Weicht von
   * `nextStep` nur im Restposten-Fall ab (`APPROVE_FIRST_MAX_RATIO`).
   */
  actionStep: JourneyStepId | null;
  /** Der Schritt, der seine Vorfahrt abgegeben hat, als Zweitaktion. */
  actionSecondaryStep: JourneyStepId | null;
  quotaBlocked: boolean;
} {
  const { ladder, action, actionSecondary } = deriveNextStep(input);

  // Entscheidung D1: `nextStep` bleibt die LEITER, nicht die Handlung.
  //
  // `ContextJourneyBand` reicht `journey.nextStep` an die `JourneyChips` weiter,
  // und `stateFor` leitet daraus ab, welcher der sechs Chips 'current' ist. Ein
  // gekippter `nextStep` haette also die Leiter im Rahmen mitbewegt: der
  // Freigabe-Chip haette seinen Zeiger verloren und der Optimieren-Chip ihn
  // bekommen, obwohl 19 Produkte nachweislich auf die Freigabe warten. Die Spec
  // (Abschnitt 5.3) verlangt ausdruecklich, dass NUR die Handlungskarte kippt.
  //
  // Aus demselben Grund rechnen `stateFor`, `countFor` und `quotaBlocked`
  // unveraendert mit `ladder`. Besonders `quotaBlocked`: es beantwortet die
  // Frage "haengt der naechste Schritt am Kontingent", und im Restposten-Fall
  // ist der naechste Schritt der Leiter die Freigabe, die kein Kontingent
  // braucht. Mit `action` haette ein erschoepftes Konto hier `true` gemeldet
  // und die Leiste haette den Freigabe-Chip nach /pricing geschickt.
  const nextStep = ladder;

  // N4 (Bericht 5.4): Bei erschoepftem Kontingent zeigt der Zeiger weiterhin auf
  // Optimieren, damit der Nutzer sieht WORAN es haengt. Der Schritt wird aber als
  // blockiert ausgewiesen, und die Leiste schickt den Klick auf /pricing statt in
  // eine Aktion, die sofort scheitert.
  const quotaBlocked = nextStep === 'optimize' && !input.canOptimize;

  // Die Zahl, die frueher als `secondary`/`rejected` NUR an der Freigabe-Stufe
  // stand, ist seit Spec 3/3.1 (Umbau auf sechs Schritte, 16.09.2026) auf
  // mehrere Schritte verteilt statt an einer angeklebt: die Wartenden
  // (`awaitingApproval`) sind jetzt die Chip-Zahl von Schritt 2, der Bestand
  // freigegebener Produkte bleibt an Schritt 3, und die Abgelehnten stehen an
  // KEINEM Chip mehr ("einen Reiter, aber keinen Chip", Spec 3.1) -- die
  // Werkbank zeigt sie ueber ihren eigenen Reiter, nicht ueber die Leiter.
  // `detailFor` traegt je Schritt genau die Tooltip-Daten, die sein Zustands-
  // wort braucht, siehe `JourneyStepDetail`.
  const steps: JourneyStep[] = ORDER.map((id) => ({
    id,
    state: quotaBlocked && id === 'optimize'
      ? ('blocked' as JourneyStepState)
      : stateFor(id, input, nextStep),
    count: countFor(id, input),
    detail: detailFor(id, input),
  }));

  return { steps, nextStep, actionStep: action, actionSecondaryStep: actionSecondary, quotaBlocked };
}
